const STORAGE_KEY = "cointervue_chunk_reload_timestamp";
const COOLDOWN_MS = 15_000;

let isReloading = false;

const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "failed to load module script",
  "dynamically imported module",
  "loading (?:css )?chunk",
  "chunkloaderror",
  "unable to preload css",
  "load failed",
  "expected a javascript-or-wasm module script",
  "mime type of [\"']text/html[\"']",
  "strict mime type checking",
  "reading ['\"]default['\"]",
  "['\"]default['\"] of (?:undefined|null)",
  "cannot read propert(?:y|ies) .*?['\"]default['\"]",
];

const CHUNK_ERROR_REGEX = new RegExp(CHUNK_ERROR_PATTERNS.join("|"), "i");

function extractErrorMessage(error: unknown): string {
  if (!error) return "" ;
  if (typeof error === "string") return error;
  if (error instanceof Error) return `${error.name} ${error.message} ${error.stack ?? ""}`;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export function isChunkLoadError(error: unknown): boolean {
  return Boolean(error) && CHUNK_ERROR_REGEX.test(extractErrorMessage(error));
}

export function handleChunkReload(error?: unknown): boolean {
  if (isReloading) return true;
  if (error !== undefined && !isChunkLoadError(error)) return false;

  try {
    const lastReload = sessionStorage.getItem(STORAGE_KEY);
    if (lastReload && Date.now() - Number(lastReload) < COOLDOWN_MS) {
      return false;
    }
    isReloading = true;
    sessionStorage.setItem(STORAGE_KEY, Date.now().toString());
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

export function initChunkErrorListener(): () => void {
  if (typeof window === "undefined") return () => {};

  const handle = (e: Event | PromiseRejectionEvent) => {
    const error =
      (e as PromiseRejectionEvent).reason ??
      (e as CustomEvent).detail ??
      (e as unknown as { payload?: unknown }).payload ??
      e;

    if (e.type === "vite:preloadError" ? handleChunkReload() : handleChunkReload(error)) {
      e.preventDefault();
    }
  };

  window.addEventListener("vite:preloadError", handle);
  window.addEventListener("unhandledrejection", handle);

  return () => {
    window.removeEventListener("vite:preloadError", handle);
    window.removeEventListener("unhandledrejection", handle);
  };
}
